import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, ArrowRight } from "lucide-react";
import { BRAND, BADGES, IMAGES } from "@/data/content";
import { useConsultation } from "@/components/site/consultation";
import { MaskLines } from "@/components/site/reveal";

export default function Hero() {
  const { open } = useConsultation();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const overlayY = useTransform(scrollYProgress, [0, 1], [0, 60]);

  return (
    <section id="top" ref={ref} className="relative min-h-screen overflow-hidden bg-navy grain" data-testid="hero-section">
      {/* Main brand logo, right side */}
      <motion.div style={{ y: imgY }} className="pointer-events-none absolute inset-y-0 right-0 hidden w-[48%] items-center justify-center lg:flex">
        <div className="absolute h-[460px] w-[460px] rounded-full bg-gold/10 blur-[130px]" />
        <motion.div
          initial={{ opacity: 0, scale: 0.94 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.2, delay: 0.4, ease: [0.16, 1, 0.3, 1] }}
          className="relative mx-10 w-full max-w-md overflow-hidden border border-gold/25 bg-[#0d0d11] shadow-[0_40px_90px_rgba(0,0,0,0.5)]"
        >
          <img src={IMAGES.logo} alt="First Impression Property Management logo" className="h-full w-full object-contain" />
        </motion.div>
      </motion.div>

      <div className="relative z-10 mx-auto flex min-h-screen max-w-7xl flex-col justify-center px-6 pt-28 pb-16 lg:px-10">
        <motion.p
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3, duration: 1 }}
          className="mb-8 flex items-center gap-3 text-[11px] uppercase tracking-[0.35em] text-gold"
        >
          <span className="h-px w-10 bg-gold" /> {BRAND.division} Division
        </motion.p>

        <h1 className="max-w-3xl font-serif text-5xl font-light leading-[0.95] tracking-tight text-white sm:text-6xl md:text-7xl lg:text-[5.5rem]">
          <MaskLines start lines={["Property", "Management"]} />
          <MaskLines start delayBase={0.24} lineClassName="italic text-gold" lines={["Made Simple."]} />
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9, duration: 0.9 }}
          className="mt-8 max-w-xl text-base leading-relaxed text-white/70 md:text-lg"
        >
          From guest changeovers to hands-off guaranteed rent, we provide flexible, end-to-end
          management backed by our own in-house cleaning and maintenance teams.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.1, duration: 0.9 }}
          className="mt-10 flex flex-col gap-4 sm:flex-row"
        >
          <button
            onClick={() => open()}
            data-testid="hero-primary-cta"
            className="group inline-flex items-center justify-center gap-2 bg-gold px-8 py-4 text-sm font-semibold uppercase tracking-[0.15em] text-navy transition-[transform,filter] duration-300 hover:-translate-y-0.5 hover:brightness-105"
          >
            Get a Free Consultation
            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </button>
          <button
            onClick={() => open()}
            data-testid="hero-secondary-cta"
            className="group inline-flex items-center justify-center gap-2 border border-white/25 px-8 py-4 text-sm font-semibold uppercase tracking-[0.15em] text-white transition-colors duration-300 hover:border-gold hover:text-gold"
          >
            Request a Management Quote
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </button>
        </motion.div>

        <motion.div style={{ y: overlayY }} className="mt-16 flex flex-wrap items-center gap-x-8 gap-y-3">
          {BADGES.map((b, i) => (
            <motion.span
              key={b}
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.3 + i * 0.15 }}
              className="flex items-center gap-3 text-xs uppercase tracking-[0.25em] text-white/60"
            >
              <span className="h-1.5 w-1.5 rounded-full bg-gold" /> {b}
            </motion.span>
          ))}
        </motion.div>
      </div>

      <div className="absolute bottom-8 left-1/2 z-10 -translate-x-1/2 text-[10px] uppercase tracking-[0.3em] text-white/40">
        Scroll
      </div>
    </section>
  );
}
